import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Button from 'components/Button';
import Toast from 'components/Toast';
import ChatConversationsPanel from 'pages/chat/ChatConversationsPanel';
import ChatQuickReplies from 'pages/chat/ChatQuickReplies';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';
import {
  AUTH_CONSTANTS,
  API_ROUTES,
  chatConversationMessagesPath,
  chatConversationPath,
} from 'utils/constants';
import {
  authorizedFetch,
  type AuthorizedFetchOptions,
  clientAuthorizedGet,
  clientAuthorizedDelete,
} from 'utils/fetchUtils';
import {
  ChatApiMessage,
  ChatConversation,
  ChatConversationDeleteResponse,
  ChatOutboundUserMessage,
} from 'types/chat';
import styles from './Chat.module.scss';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  error?: boolean;
}

interface StreamChunk {
  message?: { content?: string };
  response?: string;
  done?: boolean;
  event?: string;
  conversationId?: string;
  topics?: string[];
  source?: string;
}

async function parseHttpErrorMessage(res: Response): Promise<string> {
  const text = await res.text();
  if (!text.trim()) {
    return `Chat error (${res.status})`;
  }
  try {
    const parsed = JSON.parse(text) as Record<string, unknown>;
    if (typeof parsed.message === 'string') return parsed.message;
    if (typeof parsed.error === 'string') return parsed.error;
  } catch {
    // not JSON
  }
  return text;
}

function apiMessagesToUi(rows: ChatApiMessage[]): Message[] {
  return [...rows]
    .sort((a, b) => a.position - b.position)
    .map((m) => ({
      id: m.id,
      role: m.role,
      content: m.content,
    }));
}

function messageRowClass(role: Message['role']): string {
  if (role === 'user') return styles.messageUser;
  if (role === 'system') return styles.messageSystem;
  if (role === 'tool') return styles.messageTool;
  return styles.messageAssistant;
}

function roleLabel(role: Message['role']): string {
  if (role === 'user') return 'You';
  if (role === 'system') return 'System';
  if (role === 'tool') return 'Tool';
  return 'AI';
}

function Chat() {
  const activeCompanyId = useActiveCompanyStore((s) => s.activeCompanyId);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [agentUsingTools, setAgentUsingTools] = useState(false);
  const [intentChecking, setIntentChecking] = useState(false);
  const [intentChecked, setIntentChecked] = useState<{
    topics: string[];
    source: string;
  } | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [conversationsRefreshing, setConversationsRefreshing] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState<
    string | null
  >(null);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [quickQuestions, setQuickQuestions] = useState<string[]>([]);
  const [quickQuestionsLoading, setQuickQuestionsLoading] = useState(false);
  /** Mobile (narrow view): conversation list overlay; hidden until user opens it */
  const [mobileConversationsOpen, setMobileConversationsOpen] = useState(false);
  const [deletingConversationId, setDeletingConversationId] = useState<
    string | null
  >(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (!mobileConversationsOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileConversationsOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileConversationsOpen]);

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    },
    [],
  );

  const refreshConversationsQuiet = useCallback(async () => {
    try {
      const list = await clientAuthorizedGet<ChatConversation[]>(
        API_ROUTES.CHAT.CONVERSATIONS,
      );
      setConversations(list);
    } catch {
      // best-effort after send
    }
  }, []);

  const loadConversations = useCallback(
    async (mode: 'initial' | 'refresh') => {
      if (mode === 'initial') setConversationsLoading(true);
      else setConversationsRefreshing(true);
      try {
        const list = await clientAuthorizedGet<ChatConversation[]>(
          API_ROUTES.CHAT.CONVERSATIONS,
        );
        setConversations(list);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to load conversations.';
        showToast(errorMessage, 'error');
      } finally {
        setConversationsLoading(false);
        setConversationsRefreshing(false);
      }
    },
    [showToast],
  );

  useEffect(() => {
    void loadConversations('initial');
  }, [loadConversations]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setQuickQuestionsLoading(true);
      try {
        const url = `${AUTH_CONSTANTS.API_BASE_URL}${API_ROUTES.CHAT.SUGGESTED_QUESTIONS}`;
        const res = await authorizedFetch(
          url,
          {
            method: 'GET',
            headers: { Accept: 'application/json' },
          },
          { skipAuth: true },
        );

        if (!res.ok) {
          if (!cancelled) setQuickQuestions([]);
          return;
        }

        const data = (await res.json()) as { questions?: unknown };
        const questions = Array.isArray(data.questions)
          ? data.questions
              .filter(
                (q): q is string =>
                  typeof q === 'string' && q.trim().length > 0,
              )
              .map((q) => q.trim())
          : [];

        if (!cancelled) setQuickQuestions(questions);
      } catch {
        if (!cancelled) setQuickQuestions([]);
      } finally {
        if (!cancelled) setQuickQuestionsLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadConversationMessages = useCallback(
    async (conversationId: string) => {
      setMessagesLoading(true);
      setMessages([]);
      try {
        const rows = await clientAuthorizedGet<ChatApiMessage[]>(
          chatConversationMessagesPath(conversationId),
        );
        setMessages(apiMessagesToUi(rows));
        setActiveConversationId(conversationId);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to load messages.';
        showToast(errorMessage, 'error');
        setActiveConversationId(null);
      } finally {
        setMessagesLoading(false);
      }
    },
    [showToast],
  );

  const handleNewChat = useCallback(() => {
    setActiveConversationId(null);
    setMessages([]);
    setInput('');
    setMobileConversationsOpen(false);
  }, []);

  const deleteConversation = useCallback(
    async (id: string) => {
      if (
        !window.confirm(
          'Delete this conversation and all of its messages? This cannot be undone.',
        )
      ) {
        return;
      }
      setDeletingConversationId(id);
      try {
        await clientAuthorizedDelete<ChatConversationDeleteResponse>(
          chatConversationPath(id),
        );
        setConversations((prev) => prev.filter((c) => c.id !== id));
        if (activeConversationId === id) {
          handleNewChat();
        }
        showToast('Conversation deleted.', 'success');
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to delete conversation.';
        showToast(errorMessage, 'error');
      } finally {
        setDeletingConversationId(null);
      }
    },
    [activeConversationId, handleNewChat, showToast],
  );

  const runStreamingRequest = useCallback(
    async (userMessages: ChatOutboundUserMessage[], assistantId: string) => {
      if (!activeCompanyId) {
        const msg = 'Select a company to use chat.';
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId ? { ...m, content: msg, error: true } : m,
          ),
        );
        showToast(msg, 'error');
        return;
      }

      const url = `${AUTH_CONSTANTS.API_BASE_URL}${API_ROUTES.CHAT.STREAM}`;
      const context: { companyId: string; conversationId?: string } = {
        companyId: activeCompanyId,
      };
      if (activeConversationId) {
        context.conversationId = activeConversationId;
      }
      const bodyPayload: {
        messages: ChatOutboundUserMessage[];
        stream: true;
        context: { companyId: string; conversationId?: string };
      } = {
        messages: userMessages,
        stream: true,
        context,
      };
      const body = JSON.stringify(bodyPayload);
      const fetchOptions: AuthorizedFetchOptions = {
        endpoint: API_ROUTES.CHAT.STREAM,
      };

      let res: Response;
      try {
        res = await authorizedFetch(
          url,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
          },
          fetchOptions,
        );
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to get response.';
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? { ...m, content: errorMessage, error: true }
              : m,
          ),
        );
        showToast(errorMessage, 'error');
        return;
      }

      if (!res.ok) {
        const errText = await parseHttpErrorMessage(res);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId ? { ...m, content: errText, error: true } : m,
          ),
        );
        showToast(errText, 'error');
        return;
      }

      const reader = res.body?.getReader();
      if (!reader) {
        const errText = 'Response body is not readable';
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId ? { ...m, content: errText, error: true } : m,
          ),
        );
        showToast(errText, 'error');
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let accumulatedContent = '';

      const processTrimmedLine = (trimmed: string) => {
        if (!trimmed) return;
        try {
          const chunk = JSON.parse(trimmed) as StreamChunk;
          if (
            chunk.event === 'chat_conversation' &&
            typeof chunk.conversationId === 'string'
          ) {
            setActiveConversationId(chunk.conversationId);
            return;
          }
          if (chunk.event === 'intent_checking') {
            setIntentChecking(true);
            setIntentChecked(null);
            return;
          }
          if (chunk.event === 'intent_checked') {
            const topics = Array.isArray(chunk.topics)
              ? chunk.topics.filter(
                  (topic): topic is string =>
                    typeof topic === 'string' && topic.trim().length > 0,
                )
              : [];
            const source =
              typeof chunk.source === 'string' && chunk.source.trim().length > 0
                ? chunk.source.trim()
                : 'default';
            setIntentChecking(false);
            setIntentChecked({ topics, source });
            return;
          }
          if (chunk.event === 'agent_using_tools') {
            setAgentUsingTools(true);
            setIntentChecking(false);
            return;
          }
          const delta = chunk.message?.content ?? chunk.response ?? '';
          if (delta) {
            accumulatedContent += delta;
            setAgentUsingTools(false);
            setIntentChecking(false);
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, content: m.content + delta } : m,
              ),
            );
          }
        } catch {
          // Skip malformed JSON lines
        }
      };

      try {
        let readResult = await reader.read();

        while (!readResult.done) {
          buffer += decoder.decode(readResult.value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            processTrimmedLine(line.trim());
          }
          readResult = await reader.read();
        }

        if (buffer.trim()) {
          processTrimmedLine(buffer.trim());
        }

        setAgentUsingTools(false);
        setIntentChecking(false);
        setIntentChecked(null);
        setMessages((prev) =>
          prev.map((m) => {
            if (m.id !== assistantId) return m;
            const content = m.content || 'No response.';
            return { ...m, content };
          }),
        );

        if (accumulatedContent.toLowerCase().includes('rate limit')) {
          showToast(
            'Too many actions right now; please try again in a minute.',
            'error',
          );
        }

        void refreshConversationsQuiet();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Stream failed.';
        setIntentChecking(false);
        setIntentChecked(null);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? {
                  ...m,
                  content:
                    m.content.trim().length > 0
                      ? `${m.content}\n\n(${errorMessage})`
                      : errorMessage,
                  error: true,
                }
              : m,
          ),
        );
        showToast(errorMessage, 'error');
      }
    },
    [
      activeCompanyId,
      activeConversationId,
      refreshConversationsQuiet,
      showToast,
    ],
  );

  const sendMessage = useCallback(
    async (textOverride?: string) => {
      const text = (textOverride ?? input.trim()).trim();
      if (!text || loading) return;

      if (!activeCompanyId) {
        showToast('Select a company to use chat.', 'error');
        return;
      }

      if (!textOverride) setInput('');
      const userMessage: Message = {
        id: crypto.randomUUID(),
        role: 'user',
        content: text,
      };
      const assistantId = crypto.randomUUID();
      const assistantMessage: Message = {
        id: assistantId,
        role: 'assistant',
        content: '',
      };
      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      setLoading(true);
      setAgentUsingTools(false);
      setIntentChecking(false);
      setIntentChecked(null);

      const outbound: ChatOutboundUserMessage[] = [
        { role: 'user', content: text },
      ];

      try {
        await runStreamingRequest(outbound, assistantId);
      } finally {
        setLoading(false);
        setAgentUsingTools(false);
      }
    },
    [activeCompanyId, input, loading, runStreamingRequest, showToast],
  );

  const retryMessage = useCallback(
    async (assistantId: string) => {
      if (loading) return;
      if (!activeCompanyId) {
        showToast('Select a company to use chat.', 'error');
        return;
      }
      const assistantIndex = messages.findIndex((m) => m.id === assistantId);
      if (assistantIndex <= 0) return;
      const userMessage = messages[assistantIndex - 1];
      if (!userMessage || userMessage.role !== 'user') return;

      const newAssistantId = crypto.randomUUID();
      setMessages((prev) => {
        const withoutError = prev.filter((m) => m.id !== assistantId);
        return [
          ...withoutError,
          {
            id: newAssistantId,
            role: 'assistant',
            content: '',
            error: undefined,
          },
        ];
      });
      setLoading(true);
      setAgentUsingTools(false);
      setIntentChecking(false);
      setIntentChecked(null);

      const outbound: ChatOutboundUserMessage[] = [
        { role: 'user', content: userMessage.content },
      ];

      try {
        await runStreamingRequest(outbound, newAssistantId);
      } finally {
        setLoading(false);
        setAgentUsingTools(false);
      }
    },
    [activeCompanyId, loading, messages, runStreamingRequest, showToast],
  );

  const lastAssistantForVoid = [...messages]
    .reverse()
    .find((m) => m.role === 'assistant' && !m.error);
  const lastAssistantContent = lastAssistantForVoid?.content ?? '';
  const isAskingVoidConfirmation =
    lastAssistantContent.length > 0 &&
    /(\b(reply\s+)?(yes|confirm)\b|confirm\s+to\s+void|void\s+.*\bconfirm\b)/i.test(
      lastAssistantContent,
    ) &&
    /void/i.test(lastAssistantContent);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void sendMessage();
      }
    },
    [sendMessage],
  );

  return (
    <div className={styles.shell}>
      <div className={styles.header}>
        <h1 className={styles.title}>Chat</h1>
        <p className={styles.subtitle}>
          Select a company first. Each send posts only new user message(s); the
          server merges prior turns when you continue a conversation.
        </p>
      </div>

      <div className={styles.layout}>
        <div
          className={styles.conversationsColumn}
          data-mobile-open={mobileConversationsOpen ? 'true' : 'false'}
          onClick={
            mobileConversationsOpen
              ? () => setMobileConversationsOpen(false)
              : undefined
          }
        >
          <div
            className={styles.conversationsPanelInner}
            onClick={(e) => e.stopPropagation()}
          >
            <ChatConversationsPanel
              conversations={conversations}
              loading={conversationsLoading}
              selectedId={activeConversationId}
              onSelect={(id) => {
                setMobileConversationsOpen(false);
                void loadConversationMessages(id);
              }}
              onNewChat={handleNewChat}
              onRefresh={() => void loadConversations('refresh')}
              refreshing={conversationsRefreshing}
              onDeleteConversation={(id) => {
                void deleteConversation(id);
              }}
              deletingConversationId={deletingConversationId}
              showMobileClose={mobileConversationsOpen}
              onMobileClose={() => setMobileConversationsOpen(false)}
            />
          </div>
        </div>

        <div className={styles.mainColumn}>
          <div className={styles.mobileToolbar}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setMobileConversationsOpen(true)}
              aria-expanded={mobileConversationsOpen}
              aria-controls="chat-conversations-panel"
              id="chat-conversations-toggle"
            >
              Conversations
            </Button>
          </div>
          <div className={styles.chatArea}>
            <div className={styles.messagesWrap}>
              {messagesLoading && (
                <div className={styles.messagesLoadingOverlay}>
                  Loading messages…
                </div>
              )}
              <div className={styles.messages}>
                {messages.length === 0 && !messagesLoading && (
                  <div className={styles.emptyState}>
                    <div>
                      {!activeCompanyId
                        ? 'Select a company (header) to use chat.'
                        : 'Send a message to start, or choose a conversation on the left.'}
                    </div>
                    {!quickQuestionsLoading && quickQuestions.length > 0 && (
                      <ChatQuickReplies
                        questions={quickQuestions}
                        disabled={loading || !activeCompanyId}
                        onQuickReply={(q) => {
                          setInput('');
                          void sendMessage(q);
                        }}
                      />
                    )}
                  </div>
                )}
                {messages.map((msg) => {
                  const isAssistantWithTools =
                    msg.role === 'assistant' &&
                    loading &&
                    agentUsingTools &&
                    !msg.content &&
                    !msg.error;
                  const isErrorReply =
                    msg.role === 'assistant' &&
                    !msg.error &&
                    msg.content &&
                    /(\bfailed\b|\berror\b|validation\s+failed|not\s+found)/i.test(
                      msg.content,
                    );
                  const showPendingStreamStatus =
                    loading &&
                    msg.role === 'assistant' &&
                    !msg.content &&
                    !msg.error;
                  let pendingStreamStatus = '...';
                  if (intentChecking) {
                    pendingStreamStatus = 'Understanding your request...';
                  } else if (intentChecked) {
                    const topicsLabel =
                      intentChecked.topics.length > 0
                        ? intentChecked.topics.join(', ')
                        : 'general';
                    pendingStreamStatus = `Intent checked (${intentChecked.source}): ${topicsLabel}`;
                  } else if (isAssistantWithTools) {
                    pendingStreamStatus = 'Working on it...';
                  }
                  return (
                    <div
                      key={msg.id}
                      className={`${messageRowClass(msg.role)} ${
                        msg.error ? styles.messageAssistant : ''
                      }`}
                    >
                      <span className={styles.messageRole}>
                        {roleLabel(msg.role)}
                      </span>
                      <div
                        className={
                          msg.error || isErrorReply
                            ? `${styles.messageContent} ${styles.messageContentError}`
                            : styles.messageContent
                        }
                      >
                        {msg.error ? (
                          <>
                            <p className={styles.errorText}>{msg.content}</p>
                            <div className={styles.retryRow}>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => void retryMessage(msg.id)}
                                disabled={loading || !activeCompanyId}
                                aria-label="Retry this message"
                              >
                                Retry
                              </Button>
                            </div>
                          </>
                        ) : showPendingStreamStatus ? (
                          <span className={styles.usingTools}>
                            {pendingStreamStatus}
                          </span>
                        ) : msg.role === 'assistant' || msg.role === 'user' ? (
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {msg.content}
                          </ReactMarkdown>
                        ) : (
                          <span>{msg.content}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            </div>

            <div className={styles.inputRow}>
              <textarea
                className={styles.input}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type a message..."
                rows={2}
                disabled={loading || messagesLoading || !activeCompanyId}
                aria-label="Chat message"
              />
              {isAskingVoidConfirmation && (
                <Button
                  onClick={() => void sendMessage('YES')}
                  disabled={loading || messagesLoading || !activeCompanyId}
                  className={styles.confirmButton}
                  variant="danger"
                >
                  Confirm
                </Button>
              )}
              <Button
                onClick={() => void sendMessage()}
                disabled={
                  !input.trim() ||
                  loading ||
                  messagesLoading ||
                  !activeCompanyId
                }
                className={styles.sendButton}
              >
                Send
              </Button>
            </div>
          </div>
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default Chat;
